import React, { useState } from 'react';
import {
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { addGym, resendGymVerificationEmail } from '../api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const cities = [
  { value: 'PAR', label: 'Paris' },
  { value: 'ROU', label: 'Rouen' },
  { value: 'LYO', label: 'Lyon' },
  { value: 'MAR', label: 'Marseille' },
  { value: 'TOU', label: 'Toulouse' },
  { value: 'BOR', label: 'Bordeaux' },
  { value: 'LIL', label: 'Lille' },
  { value: 'NAN', label: 'Nantes' },
  { value: 'STR', label: 'Strasbourg' },
  { value: 'REN', label: 'Rennes' },
  { value: 'GRE', label: 'Grenoble' },
  { value: 'MPL', label: 'Montpellier' },
  { value: 'CAEN', label: 'Caen' },
  { value: 'HAV', label: 'Le Havre' },
  { value: 'AMI', label: 'Amiens' },
  { value: 'DIJ', label: 'Dijon' },
  { value: 'NOR', label: 'Normandie' },
];

const LocationMarker = ({ setLatLon }) => {
  const [position, setPosition] = useState(null);

  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      setLatLon(lat, lng);
    },
  });

  return position ? <Marker position={position} /> : null;
};

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    address: '',
    lat: '',
    lon: '',
    city: '',
    phone_number: '',
    owner_email: '',
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleLatLonChange = (lat, lon) => {
    setFormData((previous) => ({
      ...previous,
      lat: lat.toString(),
      lon: lon.toString(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.lat || !formData.lon) {
      setSnackbarMessage("Sélectionnez l'emplacement de la salle sur la carte.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const result = await addGym(formData);
      setRegistrationResult(result);
    } catch (error) {
      setSnackbarMessage(error.message || "La salle n'a pas pu être créée. Veuillez réessayer.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const result = await resendGymVerificationEmail(formData.owner_email);
      setSnackbarMessage(result.message);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setRegistrationResult((previous) => ({
        ...previous,
        email_sent: true,
      }));
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setResending(false);
    }
  };

  return (
      <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{
            minHeight: '100vh',
            bgcolor: '#eef2f6',
            p: 3,
          }}
      >
        <Grid item xs={12} sm={10} md={8} lg={6}>
          <Card
              elevation={6}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 8px 20px rgba(0,0,0,0.05)',
              }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography variant="h4" gutterBottom align="center" fontWeight={600}>
                Inscription de votre salle
              </Typography>

              <Divider sx={{ my: 2 }} />

              {registrationResult ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h5" fontWeight={700} color="#010449" gutterBottom>
                    Votre salle a été enregistrée
                  </Typography>
                  <Alert severity={registrationResult.email_sent ? 'success' : 'warning'} sx={{ mt: 2 }}>
                    {registrationResult.message}
                  </Alert>
                  <Typography color="text.secondary" sx={{ mt: 2, lineHeight: 2 }}>
                    Le lien de confirmation sera envoyé à {formData.owner_email}. Pensez à vérifier le dossier des courriers indésirables.
                  </Typography>
                  <Button
                    onClick={handleResendEmail}
                    disabled={resending}
                    variant="outlined"
                    sx={{ mt: 3, borderRadius: 3 }}
                  >
                    {resending ? <CircularProgress size={22} /> : "Renvoyer l'e-mail"}
                  </Button>
                </Box>
              ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                        label="Nom de la salle"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                        label="Ville"
                        name="city"
                        select
                        value={formData.city}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        variant="outlined"
                    >
                      {cities.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        align="center"
                        fontWeight={500}
                        color="text.secondary"
                    >
                      Emplacement de la salle
                    </Typography>
                    <Box
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          mt: 1,
                          border: '1px solid #ddd',
                        }}
                    >
                      <MapContainer center={[48.8566, 2.3522]} zoom={13} style={{ height: '400px', width: '100%' }}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution="&copy; OpenStreetMap contributors"
                        />
                        <LocationMarker setLatLon={handleLatLonChange} />
                      </MapContainer>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                        label="Adresse"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                        label="Adresse e-mail du responsable"
                        name="owner_email"
                        type="email"
                        value={formData.owner_email}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        inputProps={{ dir: 'ltr' }}
                        variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                        label="Téléphone mobile"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        inputProps={{ dir: 'ltr' }}
                        variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                        label="Mot de passe"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                        type="submit"
                        disabled={submitting}
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 3,
                          fontSize: '1rem',
                          textTransform: 'none',
                        }}
                    >
                      S'inscrire
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              )}
            </CardContent>
          </Card>

          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Grid>
      </Grid>
  );
};

export default Signup;
